/**
 * Serverzustand der Ansichten, Bausteine, Vorlagen und Makros.
 *
 * Diese vier aendern sich selten -- sie werden einmal eingerichtet und dann
 * benutzt. Deshalb halten sie ihre Daten laenger als der Standard: Der
 * Bausteinvorrat muss nicht bei jedem Wechsel des Reiters neu geholt werden.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import { ticketKeys } from '../tickets/queries';
import {
  applyMacro,
  createMacro,
  createSavedView,
  createSnippet,
  createTemplate,
  deleteMacro,
  deleteSavedView,
  deleteSnippet,
  deleteTemplate,
  listMacros,
  listPlaceholders,
  listSavedViews,
  listSnippets,
  listTemplates,
  renderSnippet,
  updateMacro,
  updateSavedView,
  updateSnippet,
  updateTemplate,
  type MacroCreate,
  type MacroUpdate,
  type SavedViewCreate,
  type SavedViewUpdate,
  type SnippetCreate,
  type SnippetUpdate,
  type TemplateCreate,
  type TemplateUpdate,
} from './api';

export const shortcutKeys = {
  views: ['saved-views'] as const,
  snippets: ['text-snippets'] as const,
  placeholders: ['text-snippets', 'placeholders'] as const,
  templates: ['ticket-templates'] as const,
  macros: ['macros'] as const,
};

const retryUnlessPermanent = (failureCount: number, error: unknown) =>
  !(error instanceof ApiError && error.isPermanent) && failureCount < 2;

/** Fuenf Minuten. Einstellungen aendern sich nicht im Minutentakt. */
const SETTINGS_STALE_TIME = 5 * 60 * 1000;

export function useSavedViews() {
  return useQuery({
    queryKey: shortcutKeys.views,
    queryFn: listSavedViews,
    retry: retryUnlessPermanent,
  });
}

export function useSnippets(enabled = true) {
  return useQuery({
    queryKey: shortcutKeys.snippets,
    queryFn: listSnippets,
    staleTime: SETTINGS_STALE_TIME,
    retry: retryUnlessPermanent,
    enabled,
  });
}

export function usePlaceholders() {
  return useQuery({
    queryKey: shortcutKeys.placeholders,
    queryFn: listPlaceholders,
    staleTime: SETTINGS_STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useTemplates() {
  return useQuery({
    queryKey: shortcutKeys.templates,
    queryFn: listTemplates,
    staleTime: SETTINGS_STALE_TIME,
    retry: retryUnlessPermanent,
  });
}

export function useMacros(enabled = true) {
  return useQuery({
    queryKey: shortcutKeys.macros,
    queryFn: listMacros,
    staleTime: SETTINGS_STALE_TIME,
    retry: retryUnlessPermanent,
    enabled,
  });
}

function useInvalidate(key: readonly string[]) {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({ queryKey: key });
  };
}

// -- Ansichten ---------------------------------------------------------------

export function useCreateSavedView() {
  const invalidate = useInvalidate(shortcutKeys.views);
  return useMutation({
    mutationFn: (payload: SavedViewCreate) => createSavedView(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateSavedView() {
  const invalidate = useInvalidate(shortcutKeys.views);
  return useMutation({
    mutationFn: ({ viewId, payload }: { viewId: string; payload: SavedViewUpdate }) =>
      updateSavedView(viewId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteSavedView() {
  const invalidate = useInvalidate(shortcutKeys.views);
  return useMutation({
    mutationFn: (viewId: string) => deleteSavedView(viewId),
    onSuccess: invalidate,
  });
}

// -- Textbausteine -----------------------------------------------------------

export function useCreateSnippet() {
  const invalidate = useInvalidate(shortcutKeys.snippets);
  return useMutation({
    mutationFn: (payload: SnippetCreate) => createSnippet(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateSnippet() {
  const invalidate = useInvalidate(shortcutKeys.snippets);
  return useMutation({
    mutationFn: ({ snippetId, payload }: { snippetId: string; payload: SnippetUpdate }) =>
      updateSnippet(snippetId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteSnippet() {
  const invalidate = useInvalidate(shortcutKeys.snippets);
  return useMutation({
    mutationFn: (snippetId: string) => deleteSnippet(snippetId),
    onSuccess: invalidate,
  });
}

export function useRenderSnippet() {
  return useMutation({
    mutationFn: ({ snippetId, ticketId }: { snippetId: string; ticketId: string | null }) =>
      renderSnippet(snippetId, ticketId),
  });
}

// -- Vorlagen ----------------------------------------------------------------

export function useCreateTemplate() {
  const invalidate = useInvalidate(shortcutKeys.templates);
  return useMutation({
    mutationFn: (payload: TemplateCreate) => createTemplate(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateTemplate() {
  const invalidate = useInvalidate(shortcutKeys.templates);
  return useMutation({
    mutationFn: ({ templateId, payload }: { templateId: string; payload: TemplateUpdate }) =>
      updateTemplate(templateId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteTemplate() {
  const invalidate = useInvalidate(shortcutKeys.templates);
  return useMutation({
    mutationFn: (templateId: string) => deleteTemplate(templateId),
    onSuccess: invalidate,
  });
}

// -- Makros ------------------------------------------------------------------

export function useCreateMacro() {
  const invalidate = useInvalidate(shortcutKeys.macros);
  return useMutation({
    mutationFn: (payload: MacroCreate) => createMacro(payload),
    onSuccess: invalidate,
  });
}

export function useUpdateMacro() {
  const invalidate = useInvalidate(shortcutKeys.macros);
  return useMutation({
    mutationFn: ({ macroId, payload }: { macroId: string; payload: MacroUpdate }) =>
      updateMacro(macroId, payload),
    onSuccess: invalidate,
  });
}

export function useDeleteMacro() {
  const invalidate = useInvalidate(shortcutKeys.macros);
  return useMutation({
    mutationFn: (macroId: string) => deleteMacro(macroId),
    onSuccess: invalidate,
  });
}

/**
 * Wendet ein Makro an.
 *
 * Ein Makro kann alles beruehren -- Felder, Zustand, Tags, Beitraege. Deshalb
 * wird danach der gesamte Ticketzweig ungueltig und nicht nur ein Teil davon:
 * Welcher Teil betroffen war, steht im Makro und nicht hier.
 */
export function useApplyMacro(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (macroId: string) => applyMacro(ticketId, macroId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ticketKeys.all });
    },
  });
}
